import { useState, useEffect } from 'react'
import styles from './CreateProjectModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function CreateProjectModal({ 
    isOpen, 
    onClose, 
    onSave,
    clients = [],
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3B82F6',
        clientId: ''
    })
    
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})

    // Handle escape key to close modal (disabled when loading)
    useEscapeKey(isOpen, onClose, !loading)

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                description: '',
                color: '#3B82F6',
                clientId: ''
            })
            setErrors({})
        }
    }, [isOpen])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.name.trim()) {
            newErrors.name = 'Project name is required'
        }
        
        if (formData.name.trim().length > 100) {
            newErrors.name = 'Project name must be less than 100 characters'
        }
        
        if (formData.description.length > 500) {
            newErrors.description = 'Description must be less than 500 characters'
        }
        
        // Validate color format (hex color)
        if (formData.color && !/^#[0-9A-F]{6}$/i.test(formData.color)) {
            newErrors.color = 'Please select a valid color'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        setLoading(true)
        
        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }
            
            apiClient.setAuthToken(apiToken)
            
            const projectData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                color: formData.color,
                client_id: formData.clientId || null
            }
            
            const response = await apiClient.createProject(projectData)
            
            // Notify parent component of successful creation
            if (onSave) {
                onSave(response)
            }
            
            onClose()
        } catch (error) {
            console.error('Failed to create project:', error)
            
            // Handle validation errors from backend
            if (error.message.includes('details')) {
                setErrors({ general: error.message })
            } else {
                setErrors({ 
                    general: error.message || 'Failed to create project. Please try again.' 
                })
            }
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    // Predefined color options
    const colorOptions = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#F97316', // Orange
        '#06B6D4', // Cyan
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
    ]

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Create New Project</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* General Error Display */}
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    {/* Project Name */}
                    <div className={styles.formGroup}>
                        <label htmlFor="projectName" className={styles.label}>
                            Project Name *
                        </label>
                        <input
                            type="text"
                            id="projectName"
                            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter project name"
                            maxLength="100"
                            autoFocus
                            disabled={loading}
                        />
                        {errors.name && (
                            <span className={styles.fieldError}>{errors.name}</span>
                        )}
                    </div>

                    {/* Description */}
                    <div className={styles.formGroup}>
                        <label htmlFor="description" className={styles.label}>
                            Description
                        </label>
                        <textarea
                            id="description"
                            className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Enter project description (optional)"
                            rows="3"
                            maxLength="500"
                            disabled={loading}
                        />
                        {errors.description && (
                            <span className={styles.fieldError}>{errors.description}</span>
                        )}
                    </div>

                    <div className={styles.inputRow}>
                        {/* Client Selection */}
                        <div className={styles.formGroup}>
                            <label htmlFor="client" className={styles.label}>
                                Client
                            </label>
                            <select
                                id="client"
                                className={styles.select}
                                value={formData.clientId}
                                onChange={(e) => handleInputChange('clientId', e.target.value)}
                                disabled={loading}
                            >
                                <option value="">No client</option>
                                {clients.map(client => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Color Selection */}
                        <div className={styles.formGroup}>
                            <label htmlFor="color" className={styles.label}>
                                Project Color *
                            </label>
                            <div className={styles.colorSection}>
                                <input
                                    type="color"
                                    id="color"
                                    className={`${styles.colorInput} ${errors.color ? styles.inputError : ''}`}
                                    value={formData.color}
                                    onChange={(e) => handleInputChange('color', e.target.value)}
                                    disabled={loading}
                                />
                                <div className={styles.colorOptions}>
                                    {colorOptions.map(color => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`${styles.colorOption} ${formData.color === color ? styles.colorOptionSelected : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleInputChange('color', color)}
                                            disabled={loading}
                                            aria-label={`Select color ${color}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            {errors.color && (
                                <span className={styles.fieldError}>{errors.color}</span>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : 'Create Project'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}